import {
  Box,
  Button,
  Inline,
  Switcher,
  Table,
  type TableProps,
  useViewport,
} from '@omnipresentgroup/design-system';
import { useEmployees } from '@omnipresentgroup/domains';
import {
  DropdownTableFilter,
  type FilterUrlMapping,
  useUrlFilterSync,
} from '@omnipresentgroup/shared';
import type { CombinedEmployee } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnFilter,
  type ColumnFiltersState,
  type ColumnOrderState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import {
  getCountryCode,
  getCountryData,
  getEmojiFlag,
  type TCountryCode,
} from 'countries-list';
import { useMemo, useState } from 'react';

import { EMPLOYEE_STATUS_CONFIG } from '../employee-status';
import { ColumnHeaders, useEmployeesTableConfig } from './hooks';

const createCountryMapping = (
  columnId: string,
  urlParameter: string,
): FilterUrlMapping => ({
  columnId,
  urlParam: urlParameter,
  urlToFilter: (countryCode: string) =>
    getCountryData(countryCode as TCountryCode)?.name || countryCode,
  filterToUrl: (countryName: string | string[]) => {
    const name = Array.isArray(countryName) ? countryName[0] : countryName;
    return getCountryCode(name) || name;
  },
  isMulti: true,
});

const createSimpleMapping = (
  columnId: string,
  urlParameter: string = columnId,
  isMulti = false,
): FilterUrlMapping => ({
  columnId,
  urlParam: urlParameter,
  isMulti,
});

const FILTER_MAPPINGS: FilterUrlMapping[] = [
  createSimpleMapping('id'),
  createCountryMapping('country', 'countryCode'),
  createSimpleMapping('type'),
  createSimpleMapping('onboardingStatus', 'status', true),
];

const getColumnConfig = <K extends keyof typeof ColumnHeaders>(key: K) => ({
  columnId: key,
  label: ColumnHeaders[key],
});

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const DEFAULT_PAGE_SIZE = 10;
const EMPTY_ARRAY: CombinedEmployee[] = [];
const EMPTY_FILTERS: ColumnFilter[] = [];
const LOADING_ARRAY = Array.from(
  { length: DEFAULT_PAGE_SIZE },
  () => ({}) as CombinedEmployee,
);

const DEFAULT_SORT: {
  id: keyof typeof ColumnHeaders;
  desc: boolean;
}[] = [
  {
    id: 'startDate',
    desc: true,
  },
];

interface EmployeesListProps<T> {
  companyId: string;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  optionsOverride?: Omit<TableOptions<T>, 'data' | 'columns'>;
  columnsOverride?: ColumnDef<T, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  onRowClick?: (row: T) => void;
  filters?: Partial<Record<keyof typeof ColumnHeaders, boolean>>;
}

export const EmployeesList = ({
  companyId,
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  onRowClick,
  filters,
}: EmployeesListProps<CombinedEmployee>) => {
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Use the URL sync hook - it handles initial load internally now
  const { clearAllFilters } = useUrlFilterSync(
    columnFilters,
    setColumnFilters,
    FILTER_MAPPINGS,
  );

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    EmployeesListProps<CombinedEmployee>['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const { data, isLoading, isError } = useEmployees({
    companyId,
  });

  const { columns } = useEmployeesTableConfig({
    columnsOverride,
  });

  const tableOptions: Omit<
    TableOptions<CombinedEmployee>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      state: {
        columnVisibility: columnVisibilityState,
        columnOrder,
        sorting,
        // Don't apply filters while loading to prevent empty state
        columnFilters: isLoading ? EMPTY_FILTERS : columnFilters,
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel(),
      onSortingChange: setSorting,
      onColumnOrderChange: setColumnOrder,
      onColumnVisibilityChange: setColumnVisibilityState,
      ...optionsOverride,
    }),
    [
      columnFilters,
      columnOrder,
      columnVisibilityState,
      optionsOverride,
      sorting,
      isLoading, // Add isLoading to deps
    ],
  );

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : (data ?? EMPTY_ARRAY),
    columns,
    ...tableOptions,
  });

  const Filters = () => {
    const { isPhone, isTablet } = useViewport();
    const isStacked = useMemo(() => isPhone || isTablet, [isPhone, isTablet]);

    return (
      <Switcher
        isStacked={isStacked}
        css={{
          flexWrap: 'wrap',
        }}
      >
        {filters?.id && (
          <Box minW={isStacked ? undefined : 270}>
            <DropdownTableFilter<CombinedEmployee, typeof ColumnHeaders>
              columnId="id"
              label={ColumnHeaders.id}
              table={table}
              columnFilters={columnFilters}
              onFilterChange={setColumnFilters}
              optionOverride={({ originalValue, originalRow }) => ({
                label: originalRow?.name || originalValue,
              })}
            />
          </Box>
        )}

        {filters?.country && (
          <Box minW={isStacked ? undefined : 270}>
            <DropdownTableFilter<CombinedEmployee, typeof ColumnHeaders>
              {...getColumnConfig('country')}
              table={table}
              columnFilters={columnFilters}
              onFilterChange={setColumnFilters}
              optionOverride={({ originalValue }) => ({
                icon: getEmojiFlag(
                  getCountryCode(originalValue) as TCountryCode,
                ),
              })}
              showCounts
              isMulti
            />
          </Box>
        )}

        {filters?.type && (
          <Box minW={isStacked ? undefined : 270}>
            <DropdownTableFilter<CombinedEmployee, typeof ColumnHeaders>
              {...getColumnConfig('type')}
              table={table}
              columnFilters={columnFilters}
              onFilterChange={setColumnFilters}
              showCounts
            />
          </Box>
        )}

        {filters?.onboardingStatus && (
          <Box minW={isStacked ? undefined : 270}>
            <DropdownTableFilter<CombinedEmployee, typeof ColumnHeaders>
              {...getColumnConfig('onboardingStatus')}
              table={table}
              columnFilters={columnFilters}
              onFilterChange={setColumnFilters}
              optionOverride={({ originalValue }) => ({
                label: EMPLOYEE_STATUS_CONFIG[originalValue].label,
              })}
              showCounts
              isMulti
            />
          </Box>
        )}

        {filters && Object.keys(filters).length > 0 && (
          <Inline align="end" justify="end">
            <Button
              variant="tertiary"
              icon="FilterOff"
              onClick={clearAllFilters} // Use the hook's clear function
            >
              Clear
            </Button>
          </Inline>
        )}
      </Switcher>
    );
  };

  return (
    <Table
      id="employees-list"
      table={table}
      loading={isLoading}
      useSorting
      usePagination
      filterSection={filters && <Filters />}
      showErrorState={isError}
      errorStateProps={{
        id: 'employees-list-error',
        title: ERROR_STATE_TITLE,
        description: ERROR_STATE_DESCRIPTION,
        callToAction: <Button onClick={window.location.reload}>Retry</Button>,
      }}
      emptyStateProps={emptyStateProps}
      onRowClick={
        onRowClick
          ? (row: CombinedEmployee) => {
              !isLoading && !isError && onRowClick(row);
            }
          : undefined
      }
    />
  );
};
